import polygonClipping from 'polygon-clipping';
import type {
  LayoutGraphicGeometry,
  LayoutGraphicPath,
  LayoutGraphicPoint,
  LayoutGraphicRect,
  PageRect,
} from './layoutTypes';

const EPSILON = 1e-9;

export interface LayoutGraphicTransform {
  readonly translate?: LayoutGraphicPoint;
  readonly scale?: LayoutGraphicPoint;
  readonly rotationDeg?: number;
  readonly origin?: LayoutGraphicPoint;
  readonly flipX?: boolean;
  readonly flipY?: boolean;
}

export type LayoutPolygonBooleanOperation =
  | 'union'
  | 'intersection'
  | 'difference'
  | 'xor';

export const clampNormalized = (value: number): number =>
  Math.min(1, Math.max(0, Number.isFinite(value) ? value : 0));

export function normalizeLayoutGraphicPoint(
  point: LayoutGraphicPoint,
): LayoutGraphicPoint {
  return { x: clampNormalized(point.x), y: clampNormalized(point.y) };
}

export function normalizeLayoutGraphicRect(
  rect: LayoutGraphicRect,
): LayoutGraphicRect {
  const x1 = clampNormalized(Math.min(rect.x, rect.x + rect.width));
  const y1 = clampNormalized(Math.min(rect.y, rect.y + rect.height));
  const x2 = clampNormalized(Math.max(rect.x, rect.x + rect.width));
  const y2 = clampNormalized(Math.max(rect.y, rect.y + rect.height));
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

export function normalizeLayoutGraphicPath(
  path: LayoutGraphicPath,
): LayoutGraphicPath {
  const points = path.points
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
    .map(normalizeLayoutGraphicPoint);
  return { points, closed: path.closed && points.length >= 3 };
}

export function normalizeLayoutGraphicGeometry(
  geometry: LayoutGraphicGeometry,
): LayoutGraphicGeometry {
  if (geometry.type === 'point') {
    return { type: 'point', point: normalizeLayoutGraphicPoint(geometry.point) };
  }
  if (geometry.type === 'rect') {
    return { type: 'rect', rect: normalizeLayoutGraphicRect(geometry.rect) };
  }
  return {
    type: 'path',
    paths: geometry.paths.map(normalizeLayoutGraphicPath).filter((path) => path.points.length > 0),
  };
}

export function rectToNormalizedPath(rect: LayoutGraphicRect): LayoutGraphicPath {
  const normalized = normalizeLayoutGraphicRect(rect);
  return {
    closed: true,
    points: [
      { x: normalized.x, y: normalized.y },
      { x: normalized.x + normalized.width, y: normalized.y },
      { x: normalized.x + normalized.width, y: normalized.y + normalized.height },
      { x: normalized.x, y: normalized.y + normalized.height },
    ],
  };
}

/** Converts every geometry form to editable normalized paths. */
export function normalizedPaths(
  geometry: LayoutGraphicGeometry,
): readonly LayoutGraphicPath[] {
  const normalized = normalizeLayoutGraphicGeometry(geometry);
  if (normalized.type === 'point') {
    return [{ points: [normalized.point], closed: false }];
  }
  return normalized.type === 'rect'
    ? [rectToNormalizedPath(normalized.rect)]
    : normalized.paths;
}

export const geometryToNormalizedPaths = normalizedPaths;

export function layoutGraphicGeometryBBox(
  geometry: LayoutGraphicGeometry,
): LayoutGraphicRect {
  const points = normalizedPaths(geometry).flatMap((path) => path.points);
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(...xs) - x,
    height: Math.max(...ys) - y,
  };
}

export const graphicGeometryBounds = layoutGraphicGeometryBBox;

export function normalizedPointToPage(
  point: LayoutGraphicPoint,
  frame: PageRect,
): LayoutGraphicPoint {
  return {
    x: frame.x + point.x * frame.width,
    y: frame.y + point.y * frame.height,
  };
}

export function pagePointToNormalized(
  point: LayoutGraphicPoint,
  frame: PageRect,
): LayoutGraphicPoint {
  return {
    x: frame.width === 0 ? 0 : (point.x - frame.x) / frame.width,
    y: frame.height === 0 ? 0 : (point.y - frame.y) / frame.height,
  };
}

export function transformLayoutGraphicPoint(
  point: LayoutGraphicPoint,
  transform: LayoutGraphicTransform,
): LayoutGraphicPoint {
  const origin = transform.origin ?? { x: 0.5, y: 0.5 };
  const scale = transform.scale ?? { x: 1, y: 1 };
  let x = point.x - origin.x;
  let y = point.y - origin.y;
  x *= scale.x * (transform.flipX ? -1 : 1);
  y *= scale.y * (transform.flipY ? -1 : 1);
  const angle = ((transform.rotationDeg ?? 0) * Math.PI) / 180;
  const rotatedX = x * Math.cos(angle) - y * Math.sin(angle);
  const rotatedY = x * Math.sin(angle) + y * Math.cos(angle);
  return {
    x: rotatedX + origin.x + (transform.translate?.x ?? 0),
    y: rotatedY + origin.y + (transform.translate?.y ?? 0),
  };
}

export function transformLayoutGraphicGeometry(
  geometry: LayoutGraphicGeometry,
  transform: LayoutGraphicTransform,
  clamp = true,
): LayoutGraphicGeometry {
  const apply = (point: LayoutGraphicPoint) => {
    const result = transformLayoutGraphicPoint(point, transform);
    return clamp ? normalizeLayoutGraphicPoint(result) : result;
  };
  if (geometry.type === 'point') return { ...geometry, point: apply(geometry.point) };
  const paths = normalizedPaths(geometry).map((path) => ({
    ...path,
    points: path.points.map(apply),
  }));
  return { type: 'path', paths };
}

export function editLayoutGraphicVertex(
  geometry: LayoutGraphicGeometry,
  pathIndex: number,
  vertexIndex: number,
  point: LayoutGraphicPoint,
): LayoutGraphicGeometry {
  if (geometry.type === 'point') {
    return pathIndex === 0 && vertexIndex === 0
      ? { ...geometry, point: normalizeLayoutGraphicPoint(point) }
      : geometry;
  }
  const paths = normalizedPaths(geometry);
  if (!paths[pathIndex]?.points[vertexIndex]) return geometry;
  return {
    type: 'path',
    paths: paths.map((path, index) => index === pathIndex
      ? {
          ...path,
          points: path.points.map((candidate, candidateIndex) =>
            candidateIndex === vertexIndex ? normalizeLayoutGraphicPoint(point) : candidate),
        }
      : path),
  };
}

export function insertLayoutGraphicVertex(
  geometry: LayoutGraphicGeometry,
  pathIndex: number,
  afterVertexIndex: number,
  point: LayoutGraphicPoint,
): LayoutGraphicGeometry {
  const paths = normalizedPaths(geometry);
  if (!paths[pathIndex]) return geometry;
  return {
    type: 'path',
    paths: paths.map((path, index) => index === pathIndex
      ? {
          ...path,
          points: [
            ...path.points.slice(0, afterVertexIndex + 1),
            normalizeLayoutGraphicPoint(point),
            ...path.points.slice(afterVertexIndex + 1),
          ],
        }
      : path),
  };
}

export function removeLayoutGraphicVertex(
  geometry: LayoutGraphicGeometry,
  pathIndex: number,
  vertexIndex: number,
): LayoutGraphicGeometry {
  const paths = normalizedPaths(geometry);
  const path = paths[pathIndex];
  if (!path || path.points.length <= (path.closed ? 3 : 2)) return geometry;
  return {
    type: 'path',
    paths: paths.map((candidate, index) => index === pathIndex
      ? { ...candidate, points: candidate.points.filter((_, i) => i !== vertexIndex) }
      : candidate),
  };
}

export function reverseLayoutGraphicGeometry(
  geometry: LayoutGraphicGeometry,
): LayoutGraphicGeometry {
  if (geometry.type === 'point') return geometry;
  return {
    type: 'path',
    paths: [...normalizedPaths(geometry)].reverse().map((path) => ({
      ...path,
      points: [...path.points].reverse(),
    })),
  };
}

export function sampleQuadraticCurve(
  start: LayoutGraphicPoint,
  control: LayoutGraphicPoint,
  end: LayoutGraphicPoint,
  segments = 24,
): LayoutGraphicPoint[] {
  const count = Math.max(1, Math.floor(segments));
  return Array.from({ length: count + 1 }, (_, index) => {
    const t = index / count;
    const u = 1 - t;
    return {
      x: u * u * start.x + 2 * u * t * control.x + t * t * end.x,
      y: u * u * start.y + 2 * u * t * control.y + t * t * end.y,
    };
  });
}

export function sampleCubicCurve(
  start: LayoutGraphicPoint,
  control1: LayoutGraphicPoint,
  control2: LayoutGraphicPoint,
  end: LayoutGraphicPoint,
  segments = 32,
): LayoutGraphicPoint[] {
  const count = Math.max(1, Math.floor(segments));
  return Array.from({ length: count + 1 }, (_, index) => {
    const t = index / count;
    const u = 1 - t;
    return {
      x: u ** 3 * start.x + 3 * u * u * t * control1.x
        + 3 * u * t * t * control2.x + t ** 3 * end.x,
      y: u ** 3 * start.y + 3 * u * u * t * control1.y
        + 3 * u * t * t * control2.y + t ** 3 * end.y,
    };
  });
}

export function sampleArc(
  center: LayoutGraphicPoint,
  radiusX: number,
  radiusY: number,
  startDeg: number,
  endDeg: number,
  segments = 32,
): LayoutGraphicPoint[] {
  const count = Math.max(1, Math.floor(segments));
  return Array.from({ length: count + 1 }, (_, index) => {
    const angle = ((startDeg + ((endDeg - startDeg) * index) / count) * Math.PI) / 180;
    return {
      x: center.x + Math.cos(angle) * Math.abs(radiusX),
      y: center.y + Math.sin(angle) * Math.abs(radiusY),
    };
  });
}

export const sampleQuadraticBezier = sampleQuadraticCurve;
export const sampleCubicBezier = sampleCubicCurve;

export function hitTestLayoutGraphicGeometry(
  geometry: LayoutGraphicGeometry,
  point: LayoutGraphicPoint,
  tolerance = 0.02,
): boolean {
  for (const path of normalizedPaths(geometry)) {
    if (path.closed && pointInPolygon(point, path.points)) return true;
    const limit = path.closed ? path.points.length : path.points.length - 1;
    for (let index = 0; index < limit; index += 1) {
      if (distanceToSegment(point, path.points[index], path.points[(index + 1) % path.points.length])
        <= tolerance) return true;
    }
    if (path.points.length === 1 && distance(point, path.points[0]) <= tolerance) return true;
  }
  return false;
}

export const hitTestGraphicGeometry = hitTestLayoutGraphicGeometry;

export function polygonBoolean(
  operation: LayoutPolygonBooleanOperation,
  subject: LayoutGraphicGeometry,
  ...clips: LayoutGraphicGeometry[]
): LayoutGraphicPathGeometryResult {
  const subjectPolygon = toMultiPolygon(subject);
  const clipPolygons = clips.map(toMultiPolygon);
  if (subjectPolygon.length === 0) return { type: 'path', paths: [] };
  try {
    let result: MultiPolygon;
    if (operation === 'union') result = polygonClipping.union(subjectPolygon, ...clipPolygons);
    else if (operation === 'intersection') {
      result = polygonClipping.intersection(subjectPolygon, ...clipPolygons);
    } else if (operation === 'xor') result = polygonClipping.xor(subjectPolygon, ...clipPolygons);
    else result = polygonClipping.difference(subjectPolygon, ...clipPolygons);
    return {
      type: 'path',
      paths: result.flatMap((polygon) => polygon.map((ring) => ({
        closed: true,
        points: withoutClosingPoint(ring).map(([x, y]) => ({ x, y })),
      }))),
    };
  } catch {
    return { type: 'path', paths: [] };
  }
}

type LayoutGraphicPathGeometryResult = Extract<LayoutGraphicGeometry, { type: 'path' }>;
type Position = [number, number];
type Ring = Position[];
type Polygon = Ring[];
type MultiPolygon = Polygon[];

function toMultiPolygon(geometry: LayoutGraphicGeometry): MultiPolygon {
  const rings = normalizedPaths(geometry)
    .filter((path) => path.closed && path.points.length >= 3)
    .map((path) => {
      const ring: Ring = path.points.map((point) => [point.x, point.y]);
      ring.push([ring[0][0], ring[0][1]]);
      return ring;
    });
  return rings.length ? [[...rings]] : [];
}

function withoutClosingPoint(ring: Ring): Ring {
  if (ring.length < 2) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  return Math.abs(first[0] - last[0]) < EPSILON
    && Math.abs(first[1] - last[1]) < EPSILON
    ? ring.slice(0, -1)
    : ring;
}

function distance(a: LayoutGraphicPoint, b: LayoutGraphicPoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function distanceToSegment(
  point: LayoutGraphicPoint,
  start: LayoutGraphicPoint,
  end: LayoutGraphicPoint,
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (Math.abs(dx) < EPSILON && Math.abs(dy) < EPSILON) return distance(point, start);
  const t = Math.max(0, Math.min(1,
    ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)));
  return distance(point, { x: start.x + t * dx, y: start.y + t * dy });
}

function pointInPolygon(point: LayoutGraphicPoint, ring: readonly LayoutGraphicPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const a = ring[i];
    const b = ring[j];
    const intersects = (a.y > point.y) !== (b.y > point.y)
      && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}
